import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import {
  BackButtonArrow,
  PageHeaderBackground,
} from '@leagueplatform/web-common-components';
import { Box, HeadingText, StackLayout } from '@leagueplatform/genesis-core';
import {
  FormStatusPill,
  FormStatusCategory,
  bandBackgroundColor,
} from './form-status-pill.component';

export interface FormStatusDetailsHeaderProps {
  // Optional YAML override; falls back to the localized STR_STATUS_DETAILS
  // string when omitted or passed as the literal "Status Details".
  heading?: string;
  statusLabel: string;
  statusCategory: FormStatusCategory;
}

// Custom hero (not stock heroBanner) because the band must recolor per
// statusCategory, the heading sits above the pill, and the back-arrow
// lives inside the band.
export const FormStatusDetailsHeader: React.FC<
  FormStatusDetailsHeaderProps
> = ({ heading, statusLabel, statusCategory }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const localizedHeading =
    heading && heading !== 'Status Details'
      ? heading
      : formatMessage({ id: 'STR_STATUS_DETAILS' });
  const backgroundColor =
    bandBackgroundColor[statusCategory] ?? bandBackgroundColor.neutral;

  return (
    <PageHeaderBackground
      height="154px"
      pageHeaderBackgroundHeight="154px"
      css={{
        '&:before': {
          backgroundColor: `${backgroundColor} !important`,
        },
        // Stacking the heading and pill outgrows the fixed band, so let the
        // band follow its content and anchor the backdrop to its lower edge.
        // PageHeaderBackground is a genesis-commons component, so the Genesis
        // `@mobile` token does not resolve here; mirror its breakpoint.
        '@media (max-width: 640.99px)': {
          height: 'auto',
          paddingBottom: '32px',
          // Masonry's layout already contributes 16px; top up to the 32px gap
          // between the band and the card in the design.
          marginBottom: '16px',
          '&:before': {
            top: 'auto',
            bottom: 0,
          },
        },
      }}
    >
      <Box css={{ width: '100%' }}>
        <StackLayout
          orientation={{ '@initial': 'horizontal', '@mobile': 'vertical' }}
          css={{
            alignItems: 'baseline',
            // StackLayout defaults to height 100%, which stretches this row over
            // the whole band and pushes the badge out from under the heading.
            '@mobile': { alignItems: 'flex-start', height: 'auto' },
          }}
        >
          <BackButtonArrow
            fontSize="heading3"
            color="onSurface.text.primary"
            marginRight="one"
            marginY="half"
            justifyContent="initial"
            onClick={() => {
              navigate(-1);
            }}
          />
          <HeadingText
            level="1"
            size={{ '@initial': 'xxl', '@mobile': 'lg' }}
            css={{
              marginBlock: '$one $one',
              '@mobile': { marginBlock: '$half $oneAndHalf' },
            }}
          >
            {localizedHeading}
          </HeadingText>
        </StackLayout>
        {statusLabel && statusCategory && (
          <StackLayout
            css={{
              flex: 1,
              paddingLeft: '50px',
              // Stretching this row would absorb the band's bottom padding and
              // push the badge away from the heading.
              '@mobile': { flex: 'none', height: 'auto', paddingLeft: 0 },
            }}
          >
            <FormStatusPill label={statusLabel} category={statusCategory} />
          </StackLayout>
        )}
      </Box>
    </PageHeaderBackground>
  );
};
